import React, { useState } from 'react';
import { formatCurrency } from '../utils/calculations';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import SEO from '../components/SEO';

const MarkupCalculator = () => {
  const [calculationType, setCalculationType] = useState('cost-to-selling');
  const [cost, setCost] = useState(1000);
  const [sellingPrice, setSellingPrice] = useState(1500);
  const [markupPercent, setMarkupPercent] = useState(50);
  const [marginPercent, setMarginPercent] = useState(33.33);
  const [quantity, setQuantity] = useState(100);
  const [result, setResult] = useState(null);

  const calculate = () => {
    let calculatedCost = cost;
    let calculatedSelling = sellingPrice;
    let calculatedMarkup = markupPercent;
    let calculatedMargin = marginPercent;
    let profit = 0;

    switch (calculationType) {
      case 'cost-to-selling':
        // Given: Cost + Markup% → Calculate Selling Price
        calculatedSelling = cost * (1 + markupPercent / 100);
        profit = calculatedSelling - cost;
        calculatedMargin = (profit / calculatedSelling) * 100;
        break;
      case 'selling-to-cost':
        // Given: Selling Price + Margin% → Calculate Cost
        calculatedCost = sellingPrice * (1 - marginPercent / 100);
        profit = sellingPrice - calculatedCost;
        calculatedMarkup = (profit / calculatedCost) * 100;
        break;
      case 'cost-selling-given':
        // Given: Both Cost & Selling → Calculate Markup & Margin
        profit = sellingPrice - cost;
        calculatedMarkup = (profit / cost) * 100;
        calculatedMargin = (profit / sellingPrice) * 100;
        calculatedCost = cost;
        calculatedSelling = sellingPrice;
        break;
      default:
        break;
    }

    const totalCost = calculatedCost * quantity;
    const totalRevenue = calculatedSelling * quantity;
    const totalProfit = profit * quantity;

    setResult({
      cost: calculatedCost,
      sellingPrice: calculatedSelling,
      markupPercent: calculatedMarkup,
      marginPercent: calculatedMargin,
      profitPerUnit: profit,
      totalCost,
      totalRevenue,
      totalProfit,
      quantity,
    });
  };

  const formatCurrencyForPDF = (amount) => 'Rs. ' + Math.round(amount).toLocaleString('en-IN');

  const downloadPDF = () => {
    if (!result) return;
    const doc = new jsPDF();
    
    doc.setFontSize(18);
    doc.text('Markup & Margin Calculation', 105, 20, { align: 'center' });
    
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleDateString('en-IN')}`, 105, 28, { align: 'center' });

    autoTable(doc, {
      startY: 40,
      head: [['Description', 'Value']],
      body: [
        ['Cost Price (per unit)', formatCurrencyForPDF(result.cost)],
        ['Selling Price (per unit)', formatCurrencyForPDF(result.sellingPrice)],
        ['Profit (per unit)', formatCurrencyForPDF(result.profitPerUnit)],
        ['Markup Percentage', `${result.markupPercent.toFixed(2)}%`],
        ['Margin Percentage', `${result.marginPercent.toFixed(2)}%`],
        ['Quantity', result.quantity.toLocaleString()],
        ['Total Cost', formatCurrencyForPDF(result.totalCost)],
        ['Total Revenue', formatCurrencyForPDF(result.totalRevenue)],
        ['Total Profit', formatCurrencyForPDF(result.totalProfit)],
      ],
      theme: 'striped',
      headStyles: { fillColor: [37, 99, 235] },
    });

    doc.save('Markup_Margin_Calculation.pdf');
  };

  return (
    <div className="calculator-page">
      <SEO 
        title="Markup Calculator"
        description="Free Markup Calculator - Calculate selling price with desired profit margin. Cost plus markup pricing."
        keywords="markup calculator, profit margin calculator, selling price calculator, cost plus markup, markup percentage calculator"
        canonicalPath="/markup"
      />
      <div className="calculator-header">
        <h1>📈 Markup & Margin Calculator</h1>
        <p>Calculate selling price, markup, margin, and profit</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div className="input-group">
            <label>What do you want to calculate?</label>
            <select value={calculationType} onChange={(e) => setCalculationType(e.target.value)}>
              <option value="cost-to-selling">Selling Price (from Cost & Markup)</option>
              <option value="selling-to-cost">Cost Price (from Selling & Margin)</option>
              <option value="cost-selling-given">Markup & Margin (from Cost & Selling)</option>
            </select>
          </div>

          {calculationType === 'cost-to-selling' && (
            <>
              <div className="input-group">
                <label>Cost Price (₹)</label>
                <input
                  type="number"
                  value={cost === '' ? '' : cost}
                  onChange={(e) => setCost(e.target.value === '' ? '' : Number(e.target.value))}
                />
              </div>
              <div className="input-group">
                <label>Markup Percentage (%)</label>
                <input
                  type="number"
                  step="0.1"
                  value={markupPercent === '' ? '' : markupPercent}
                  onChange={(e) => setMarkupPercent(e.target.value === '' ? '' : Number(e.target.value))}
                />
                <small style={{ color: '#64748b', fontSize: '12px' }}>% added to cost price</small>
              </div>
            </>
          )}

          {calculationType === 'selling-to-cost' && (
            <>
              <div className="input-group">
                <label>Selling Price (₹)</label>
                <input
                  type="number"
                  value={sellingPrice === '' ? '' : sellingPrice}
                  onChange={(e) => setSellingPrice(e.target.value === '' ? '' : Number(e.target.value))}
                />
              </div>
              <div className="input-group">
                <label>Margin Percentage (%)</label>
                <input
                  type="number"
                  step="0.1"
                  value={marginPercent === '' ? '' : marginPercent}
                  onChange={(e) => setMarginPercent(e.target.value === '' ? '' : Number(e.target.value))}
                />
                <small style={{ color: '#64748b', fontSize: '12px' }}>% of selling price as profit</small>
              </div>
            </>
          )}

          {calculationType === 'cost-selling-given' && (
            <>
              <div className="input-group">
                <label>Cost Price (₹)</label>
                <input
                  type="number"
                  value={cost === '' ? '' : cost}
                  onChange={(e) => setCost(e.target.value === '' ? '' : Number(e.target.value))}
                />
              </div>
              <div className="input-group">
                <label>Selling Price (₹)</label>
                <input
                  type="number"
                  value={sellingPrice === '' ? '' : sellingPrice}
                  onChange={(e) => setSellingPrice(e.target.value === '' ? '' : Number(e.target.value))}
                />
              </div>
            </>
          )}

          <div className="input-group">
            <label>Quantity (for bulk calculation)</label>
            <input
              type="number"
              value={quantity === '' ? '' : quantity}
              onChange={(e) => setQuantity(e.target.value === '' ? '' : Number(e.target.value))}
            />
          </div>

          <button className="btn btn-primary" style={{ width: '100%', marginTop: '10px' }} onClick={calculate}>
            Calculate
          </button>
        </div>

        <div className="calculator-results">
          {result ? (
            <>
              <div className="result-summary" style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>
                <h3>Profit Per Unit</h3>
                <div className="amount">{formatCurrency(result.profitPerUnit)}</div>
                <p style={{ opacity: 0.9, marginTop: '8px' }}>
                  Markup: {result.markupPercent.toFixed(2)}% | Margin: {result.marginPercent.toFixed(2)}%
                </p>
              </div>

              <div className="result-details">
                <div className="result-item">
                  <span className="label">Cost Price</span>
                  <span className="value">{formatCurrency(result.cost)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Selling Price</span>
                  <span className="value">{formatCurrency(result.sellingPrice)}</span>
                </div>
                <div className="result-item" style={{ background: '#ecfdf5' }}>
                  <span className="label">Profit Per Unit</span>
                  <span className="value" style={{ color: '#10b981' }}>{formatCurrency(result.profitPerUnit)}</span>
                </div>
                <div className="result-item" style={{ background: '#dbeafe' }}>
                  <span className="label">Markup %</span>
                  <span className="value" style={{ color: '#2563eb' }}>{result.markupPercent.toFixed(2)}%</span>
                </div>
                <div className="result-item" style={{ background: '#fef3c7' }}>
                  <span className="label">Margin %</span>
                  <span className="value" style={{ color: '#f59e0b' }}>{result.marginPercent.toFixed(2)}%</span>
                </div>

                <div style={{ marginTop: '16px', padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>
                  <h4 style={{ marginBottom: '12px' }}>Bulk Calculation ({result.quantity} units)</h4>
                  <div className="result-item">
                    <span className="label">Total Cost</span>
                    <span className="value">{formatCurrency(result.totalCost)}</span>
                  </div>
                  <div className="result-item">
                    <span className="label">Total Revenue</span>
                    <span className="value">{formatCurrency(result.totalRevenue)}</span>
                  </div>
                  <div className="result-item" style={{ background: '#ecfdf5' }}>
                    <span className="label">Total Profit</span>
                    <span className="value" style={{ color: '#10b981', fontWeight: '600' }}>{formatCurrency(result.totalProfit)}</span>
                  </div>
                </div>
              </div>

              <button className="btn btn-secondary" style={{ width: '100%', marginTop: '20px' }} onClick={downloadPDF}>
                Download PDF Report
              </button>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#64748b' }}>
              <p>Enter values to calculate markup and margin</p>
            </div>
          )}
        </div>
      </div>

      <div className="info-box">
        <h4>📌 Markup vs Margin</h4>
        <ul>
          <li><strong>Markup:</strong> % added to cost → (Selling - Cost) / Cost × 100</li>
          <li><strong>Margin:</strong> % of selling price → (Selling - Cost) / Selling × 100</li>
          <li>Example: Cost ₹100, Selling ₹150 → Markup 50%, Margin 33.33%</li>
          <li>Same profit, different perspective!</li>
        </ul>
        <table className="schedule-table" style={{ marginTop: '12px' }}>
          <thead>
            <tr><th>Markup</th><th>Margin</th></tr>
          </thead>
          <tbody>
            <tr><td>25%</td><td>20%</td></tr>
            <tr><td>50%</td><td>33.33%</td></tr>
            <tr><td>100%</td><td>50%</td></tr>
            <tr><td>200%</td><td>66.67%</td></tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MarkupCalculator;
